import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Col as GridSystemCol, Hidden } from 'react-grid-system';
/*
 * all of the exported grid component extent the spacing mixin from foundation
 *   via ${props => props.theme.ns().spacing.getSpacingMixin()};
 *
 *   ex:
 *     <Col marginBottom={'xs'} />
 *
 * https://foundation.in.zillow.net/foundation-api.html#section-spacing
 */

import colMixin from '../../themes/components/Col';
import T_SHIRT_SIZES from '../../constants/T_SHIRT_SIZES';

const StyledCol = styled(
  ({ alignHorizontal, alignVertical, marginBottom, noPadding, paddingX, paddingY, useSpacingMixin, ...rest }) => (
    <GridSystemCol {...rest} />
  ),
)`
  ${colMixin}
`;

const getHiddenSizes = (props) => {
  const tShirtSizes = Object.keys(T_SHIRT_SIZES);
  const hiddenSizes = {};
  tShirtSizes.forEach((size) => {
    if (props[size] === 0) {
      hiddenSizes[size] = true;
    }
  });

  return hiddenSizes;
};

/**
 * Used in conjunction with `Row`, used to define grid columns in responsive layouts
 *
 * Please refer to the links below for more documentation:
 *
 * https://sealninja.github.io/react-grid-system/
 *
 * https://github.com/sealninja/react-grid-system
 */
const Col = (props) => {
  const hiddenSizes = getHiddenSizes(props);

  return (
    <Hidden {...hiddenSizes}>
      <StyledCol data-test="col" {...props} />
    </Hidden>
  );
};

Col.propTypes = {
  /**
   * Content of the column
   */
  children: PropTypes.node,
  /**
   * The width of the column for screenclass `xs`, either a number between 0 and 12, or "content"
   */
  xs: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['content'])]),
  /**
   * The width of the column for screenclass `sm`, either a number between 0 and 12, or "content"
   */
  sm: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['content'])]),
  /**
   * The width of the column for screenclass `md`, either a number between 0 and 12, or "content"
   */
  md: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['content'])]),
  /**
   * The width of the column for screenclass `lg`, either a number between 0 and 12, or "content"
   */
  lg: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['content'])]),
  /**
   * The width of the column for screenclass `xl`, either a number between 0 and 12, or "content"
   */
  xl: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['content'])]),
  /**
   * The width of the column for screenclass `xxl`, either a number between 0 and 12, or "content"
   */
  xxl: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['content'])]),
  /**
   * A fixed width of the column for all screenclasses"
   */
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * The offset of this column for all screenclasses
   */
  offset: PropTypes.shape({
    xs: PropTypes.number,
    sm: PropTypes.number,
    md: PropTypes.number,
    lg: PropTypes.number,
    xl: PropTypes.number,
    xxl: PropTypes.number,
  }),
  /**
   * The amount this column is pushed to the right for all screenclasses
   */
  push: PropTypes.shape({
    xs: PropTypes.number,
    sm: PropTypes.number,
    md: PropTypes.number,
    lg: PropTypes.number,
    xl: PropTypes.number,
    xxl: PropTypes.number,
  }),
  /**
   * The amount this column is pulled to the left for all screenclasses
   */
  pull: PropTypes.shape({
    xs: PropTypes.number,
    sm: PropTypes.number,
    md: PropTypes.number,
    lg: PropTypes.number,
    xl: PropTypes.number,
    xxl: PropTypes.number,
  }),
  /**
   * Optional styling
   */
  style: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  /**
   * Set to apply some debug styling
   */
  debug: PropTypes.bool,
  /**
   * Use your own component
   */
  component: PropTypes.elementType,
  /**
   * Align content horizontally (deprecated, replace with alignHorizontal)
   */
  align: PropTypes.string,
  /**
   * Align content horizontally
   */
  alignHorizontal: PropTypes.string,
  /**
   * Align content vertically
   */
  alignVertical: PropTypes.string,
  /**
   * remove default padding on component
   */
  noPadding: PropTypes.bool,
};

Col.defaultProps = {
  children: null,
  xs: null,
  sm: null,
  md: null,
  lg: null,
  xl: null,
  xxl: null,
  width: null,
  offset: {},
  push: {},
  pull: {},
  style: {},
  debug: false,
  component: 'div',
  align: null,
  alignHorizontal: null,
  alignVertical: null,
  noPadding: false,
};

/** @component */
export default Col;
